import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Layers, GitMerge } from "lucide-react";
import {
  GEMENSAM,
  getDisciplineLabel,
  getQuestionDisciplines,
} from "@/lib/fragorData/disciplines";
import type { RiskGroup } from "@/lib/basProcess/consolidation";

interface Props {
  groups: RiskGroup[];
}

/**
 * Visar hur de samordnade riskerna fördelar sig per disciplin och
 * lyfter fram frågor som berör flera discipliner (samordningspunkter).
 */
export const DisciplineOverview: React.FC<Props> = ({ groups }) => {
  const { counts, crossGroups, commonCount } = React.useMemo(() => {
    const counts: Record<string, number> = {};
    const crossGroups: Array<{ key: string; question: string; disciplines: string[] }> = [];
    let commonCount = 0;

    groups.forEach((g) => {
      const disciplines = getQuestionDisciplines({
        id: g.key,
        text: g.question,
        kategori: g.category,
      } as never);

      if (disciplines.length === 1 && disciplines[0] === GEMENSAM) {
        commonCount += 1;
        return;
      }

      const real = disciplines.filter((d) => d !== GEMENSAM);
      real.forEach((d) => {
        counts[d] = (counts[d] || 0) + 1;
      });
      if (real.length > 1) {
        crossGroups.push({ key: g.key, question: g.question, disciplines: real });
      }
    });

    return { counts, crossGroups, commonCount };
  }, [groups]);

  const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);

  if (sorted.length === 0 && commonCount === 0) return null;

  return (
    <Card>
      <CardContent className="space-y-4 p-4">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Layers className="h-4 w-4 text-muted-foreground" />
          Fördelning per disciplin
        </div>

        <div className="flex flex-wrap gap-2">
          {sorted.map(([key, count]) => (
            <Badge key={key} variant="secondary" className="gap-1">
              {getDisciplineLabel(key)}
              <span className="text-muted-foreground">{count}</span>
            </Badge>
          ))}
          {commonCount > 0 && (
            <Badge variant="outline" className="gap-1">
              Gemensamma
              <span className="text-muted-foreground">{commonCount}</span>
            </Badge>
          )}
        </div>

        {crossGroups.length > 0 && (
          <div className="space-y-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-3">
            <div className="flex items-center gap-2 text-sm font-medium">
              <GitMerge className="h-4 w-4 text-amber-600" />
              Samordningspunkter mellan discipliner ({crossGroups.length})
            </div>
            <ul className="space-y-1 text-sm">
              {crossGroups.slice(0, 8).map((g) => (
                <li key={g.key} className="flex flex-wrap items-center gap-2">
                  <span>{g.question}</span>
                  <span className="text-xs text-muted-foreground">
                    {g.disciplines.map(getDisciplineLabel).join(" · ")}
                  </span>
                </li>
              ))}
            </ul>
            {crossGroups.length > 8 && (
              <p className="text-xs text-muted-foreground">
                och {crossGroups.length - 8} till i listan nedan.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DisciplineOverview;
